import { z } from 'zod';
import api from '~/utils/api';
import { ClaimType, ResolutionMethod } from '~/utils/constants';
import { makeLineItemClaimLoaderWithReason, redirect } from '~/utils/routing';
import { lineItemClaimSchema } from '~/utils/schemas';

const paramsSchema = z.object({
  idFromPlatform: z.string(),
  claimType: z.nativeEnum(ClaimType),
  store: z.string(),
});

export const { loader, useLoaderData: useResolutionData } =
  makeLineItemClaimLoaderWithReason(({ context, params }, appState) => {
    const { idFromPlatform, store } = paramsSchema.parse(params);
    const { claim, claimType } = context;
    if (claimType === ClaimType.warranty) {
      const parsedClaim = lineItemClaimSchema.parse({
        ...claim,
        requestedResolutionMethodEnum: ResolutionMethod.warrantyReview,
      });

      appState.dispatch(appState.actions.completeLineItemClaim(parsedClaim));

      return redirect(`/${store}/order/${idFromPlatform}/${claimType}`);
    }

    const { storefrontId } = context.settings;
    const { shouldPrioritizeCredit, creditDetailText, creditHeadlineText } =
      context.settings.theme;

    const { idFromPlatform: orderIdFromPlatform } = context.order;
    const { isRefundEnabled, isGiftCardEnabled, isExchangeEnabled } =
      context.settings.return;

    const {
      return: {
        isExchangeOnly,
        // isEligible - as in within the time window
        exchange: { isEligible: isExchangeEligible },
        refund: { isEligible: isRefundEligible },
        giftCard: { isEligible: isGiftCardEligible },
      },
      productIdFromPlatform,
    } = claim.lineItem;

    const canExchange = isExchangeEnabled && isExchangeEligible;
    const canCredit =
      ((isRefundEnabled && isRefundEligible) ||
        (isGiftCardEnabled && isGiftCardEligible)) &&
      !isExchangeOnly;

    const variantData =
      canExchange && productIdFromPlatform ?
        api
          .fetchProductVariantInfo({
            params: {
              storefrontId,
              idFromPlatform: productIdFromPlatform,
            },
          })
          .then((results) =>
            results.map((result) => ({
              ...result,
              options: result.options?.sort((a, b) => a.position - b.position),
            })),
          )
      : Promise.resolve([]);

    return variantData
      .then((data) => ({
        exchangeProducts: data,
        claim,
        canExchange: canExchange && data.length > 0,
        orderId: `${orderIdFromPlatform}`,
        shouldPrioritizeCredit: !!shouldPrioritizeCredit,
        creditHeadlineText: creditHeadlineText ?? 'Not looking to exchange?',
        creditDetailText:
          creditDetailText ?? 'Please continue to finish your request.',
        canCredit,
        ...(canCredit && {
          creditResolutionMethod:
            isRefundEnabled && isRefundEligible ?
              ResolutionMethod.refund
            : ResolutionMethod.giftCard,
        }),
      }))
      .catch((err: unknown) =>
        err instanceof Error ? err : (
          new Error('Unknown error', { cause: { originalError: err } })
        ),
      );
  });
